/**
 * Options trading charges + P&L helpers — THE single implementation for the
 * browser. The server-side risk engine (engine/paper_risk.py) mirrors these
 * formulas in Python; BOTH read their rates from shared/charge_rates.json,
 * and both sides pin the same numeric examples in tests
 * (src/charges.test.ts <-> tests/test_paper_risk.py's test_matches_js_formula)
 * so a drift between the two engines fails a build, not a trader.
 *
 * Formerly the third copy of this math lived inline in static/paper.html —
 * extraction #1 of the strangler-pattern decomposition (see ../README.md).
 */

import rates from "../../shared/charge_rates.json";

export interface ChargeRates {
  sttSellPct: number;
  stampDutyPct: number;
  exchangeChargesPct: number;
  sebiChargesPct: number;
  miscChargesPct: number;
  gstPct: number;
  brokeragePerTrade: number;
}

export const CHARGE_RATES: ChargeRates = rates;

export interface ChargeBreakdown {
  stt: number;
  stampDuty: number;
  exchangeCharges: number;
  sebiCharges: number;
  miscCharges: number;
  brokerage: number;
  gst: number;
  total: number;
}

export type Side = "BUY" | "SELL";

/**
 * Round to paise (2dp), HALF-UP — the one rounding used across the money math
 * (F6). The tiny epsilon nudge pulls a value that is mathematically N.xx5 but
 * stored just below the binary-float boundary back over it, so this matches
 * Python's Decimal(...).quantize(HALF_UP) — the two engines must agree to the
 * paise. For values not on a half-paise boundary the nudge is far smaller than
 * a paise and changes nothing.
 */
export function round2(x: number): number {
  return Math.round((x + (x >= 0 ? 1 : -1) * 1e-9) * 100) / 100;
}

/**
 * Real cost of one completed round trip — decimal, paisa-exact. `openSide` is
 * the side of the OPENING leg; entry/exit are per-unit premiums; qty in units
 * (not lots). Same MEANING and rates as before; every line is now rounded to
 * paise (broker contract-note style), total = sum of the rounded lines. Mirror
 * of engine/paper_risk.py compute_charges().
 */
export function computeCharges(
  openSide: Side,
  entryPrice: number,
  exitPrice: number,
  qty: number,
): ChargeBreakdown {
  const buyPrice = openSide === "BUY" ? entryPrice : exitPrice;
  const sellPrice = openSide === "BUY" ? exitPrice : entryPrice;

  const buyPremium = qty * buyPrice;
  const sellPremium = qty * sellPrice;
  const totalPremium = buyPremium + sellPremium;

  // gst is charged on the FULL-precision exchange+misc+sebi (unchanged formula),
  // then rounded — like the individual lines.
  const exchFull = CHARGE_RATES.exchangeChargesPct * totalPremium;
  const sebiFull = CHARGE_RATES.sebiChargesPct * totalPremium;
  const miscFull = CHARGE_RATES.miscChargesPct * totalPremium;

  const stt = round2(CHARGE_RATES.sttSellPct * sellPremium);
  const stampDuty = round2(CHARGE_RATES.stampDutyPct * totalPremium);
  const exchangeCharges = round2(exchFull);
  const sebiCharges = round2(sebiFull);
  const miscCharges = round2(miscFull);
  const brokerage = round2(CHARGE_RATES.brokeragePerTrade);
  const gst = round2(CHARGE_RATES.gstPct * (exchFull + miscFull + sebiFull));
  const total = round2(stt + stampDuty + exchangeCharges + sebiCharges + miscCharges + brokerage + gst);

  return { stt, stampDuty, exchangeCharges, sebiCharges, miscCharges, brokerage, gst, total };
}

/**
 * Net P&L of a saved trade row. Trades saved before the charges feature have
 * no netPnl — fall back to gross pnl (0 if even that is absent).
 */
export function tradeNetPnl(h: { netPnl?: number | null; pnl?: number | null }): number {
  return h.netPnl != null ? h.netPnl : h.pnl ?? 0;
}
